import { FormatPreset } from '@doss/prisma/types'
import { NumberParser } from '@internationalized/number'
import { BigNumber } from 'bignumber.js'
import { DossError } from './doss-error.js'
import { LocaleCode } from './locales.js'
import {
  FormatOptions,
  NumberNegativeStyle,
  safeParseFormatOptions,
} from './types/format-options.js'

/**
 * Given a decimal value and the number of digits after the decimal, return the percent equivalent.
 * i.e. Given 0.1 and 0, return 10
 * i.e. given 0.12345 and 2, return 12.35
 */
export function decimalToPercent(decimal: number, numDecimalDigits = 2): number {
  return safeMultiply(decimal, 100, numDecimalDigits)
}

/**
 * DuckDB and Postgres run aggregations on underlying data in different orders which can
 * introduce floating point errors. For values that differ within a certain threshold / epsilon,
 * we can assume that they are effectively the same.
 */
export const FP_PRECISION_EPSILON_DEFAULT = 1e-10

/**
 * How far below the least-significant displayed digit the noise floor sits.
 * Example: At 2 decimals, the threshold will be 1e-6.
 */
const FP_PRECISION_EPSILON_FACTOR = 1e-4

/**
 * Lower bound on the noise threshold from `FP_PRECISION_EPSILON_FACTOR` above. If the number of
 * decimals is very large, ex. "10 decimal places", then we still want to round when reasonable.
 */
const FP_PRECISION_EPSILON_FLOOR = 1e-7

/**
 * Rounds a value to zero if its absolute value is tiny enough to assume it is a noisy floating
 * point precision situation. Simplifies display logic where a negative nonzero value like
 * `-1.23e-14` may otherwise be rounded, then rendered to show "-0.00".
 *
 * Pass `decimalPlaces` wherever the render precision is known, i.e. "2 decimal places".
 */
function roundEpsilonToZeroForDisplay(value: number, decimalPlaces?: number): number {
  // If we know the display precision, i.e. "N decimal places", scale the threshold by FACTOR to
  // sit four decades below the last digit the user can see. If N is very large, then we still use
  // at least the FLOOR so that we can round at reasonable thresholds.
  // If the precision is not known, then use the DEFAULT.
  const displayEpsilon =
    decimalPlaces === undefined
      ? FP_PRECISION_EPSILON_DEFAULT
      : Math.max(FP_PRECISION_EPSILON_FACTOR * 10 ** -decimalPlaces, FP_PRECISION_EPSILON_FLOOR)

  return Math.abs(value) < displayEpsilon ? 0 : value
}

/** Fallback when a currency code is well-formed but `Intl` doesn't know its decimal places. */
const DEFAULT_CURRENCY_FRACTION_DIGITS = 2

/**
 * The number of fraction digits for a currency, per ISO 4217. Most are 2, but some like
 * JPY are 0, and others like KWD are 3.
 */
function getCurrencyFractionDigits(currency: string, locale: string): number {
  try {
    const { maximumFractionDigits } = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).resolvedOptions()
    return maximumFractionDigits ?? DEFAULT_CURRENCY_FRACTION_DIGITS
  } catch {
    // We shouldn't throw failures based off errors in this non-critical logic.
    return DEFAULT_CURRENCY_FRACTION_DIGITS
  }
}

/**
 * Returns the decimal places a preset will actually render with, applying the per-preset
 * defaults. Returns `undefined` when the preset renders unrounded.
 * - Integer: 0
 * - Percent: 2 (fallback)
 * - Currency: the currency's own ISO 4217 digits
 */
function getRenderedDecimalPlaces(
  formatPreset: FormatPreset,
  formatOptions: FormatOptions,
): number | undefined {
  if (formatPreset === FormatPreset.NUMBER_INTEGER) {
    return 0
  }

  const decimalPlaces =
    'decimalPlaces' in formatOptions
      ? (formatOptions.decimalPlaces as number | undefined)
      : undefined

  if (formatPreset === FormatPreset.NUMBER_CURRENCY && decimalPlaces === undefined) {
    const { currency, locale } = formatOptions as FormatOptions<typeof FormatPreset.NUMBER_CURRENCY>
    return getCurrencyFractionDigits(currency, locale)
  }
  if (formatPreset === FormatPreset.NUMBER_PERCENT) {
    return decimalPlaces ?? 2
  }
  return decimalPlaces
}

/**
 * Formats a number as a percentage in the specified locale.
 * Accepts the same options as Intl.NumberFormat, defaults to 2 decimal places.
 *
 * i.e. Given 0.2345, returns "23.45%" in en-US, "23,45 %" in de-DE
 */
export function formatNumberAsPercentage(
  number: number,
  locale: LocaleCode = LocaleCode.EN_US,
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: options?.maximumFractionDigits ?? 2,
    ...options,
  }).format(number)
}

/**
 * Given a percent value and the number of digits after the decimal, return the decimal equivalent.
 * i.e. Given 10, return 0.1
 * i.e. Given 25.53 and 2, return 0.26
 */
export function percentToDecimal(percent: number, numDecimalDigits = 2): number {
  return safeDivide(percent, 100, 0, numDecimalDigits)
}

/**
 * Prevents dividing by 0 and returning NaN. Instead, the caller provides a safe failureValue to be
 * used when dividing by 0 is attempted.
 *
 * Rounding behavior:
 * If numDecimalDigits is specified, round to the specified number of decimal digits.
 * Otherwise, round to 10 decimal places as a cap, because division could result in a very large number of decimal places.
 */
export function safeDivide(
  numerator: number,
  denominator: number,
  failureValue: number,
  numDecimalDigits?: number,
): number {
  if (denominator === 0) {
    return failureValue
  }

  return new BigNumber(numerator)
    .div(denominator)
    .decimalPlaces(numDecimalDigits ?? 10)
    .toNumber()
}

/**
 * Ensures that the result of multiplying two numbers is rounded to the specified number of decimal
 * digits. This prevents returning odd results due to floating point errors.
 *
 * Rounding behavior:
 * If numDecimalDigits is specified, round to the specified number of decimal digits.
 * Otherwise, round to 10 decimal places as a cap, because multiplication could result in a very large number of decimal places.
 */
export function safeMultiply(
  multiplicand: number,
  multiplier: number,
  numDecimalDigits?: number,
): number {
  return new BigNumber(multiplicand)
    .multipliedBy(multiplier)
    .decimalPlaces(numDecimalDigits ?? 10)
    .toNumber()
}

/**
 * Multiplies multiple (N array) numbers safely, ensuring the result is rounded to the specified number of
 * decimal digits to prevent odd results due to floating point errors.
 *
 * Rounding behavior:
 * If numDecimalDigits is specified, round to the specified number of decimal digits.
 * Otherwise, round to 10 decimal places as a cap, because multiplication could result in a very large number of decimal places.
 */
export function safeMultiplyN(numbers: number[], numDecimalDigits?: number): number {
  const result = numbers
    .map((number) => new BigNumber(number))
    .reduce((acc, number) => acc.multipliedBy(number), new BigNumber(1))

  return result.decimalPlaces(numDecimalDigits ?? 10).toNumber()
}

/**
 * Ensures that the result of adding numbers is rounded to the specified number of decimal digits
 *
 * Rounding behavior:
 * If numDecimalDigits is specified, round to the specified number of decimal digits.
 * Otherwise, no rounding is performed.
 */
export function safeSum(summands: number[], numDecimalDigits?: number): number {
  const result = summands
    .map((number) => new BigNumber(number))
    .reduce((acc, number) => acc.plus(number), new BigNumber(0))
  return numDecimalDigits !== undefined
    ? result.decimalPlaces(numDecimalDigits).toNumber()
    : result.toNumber()
}

/**
 * Ensures that the result of subtracting numbers is rounded to the specified number of decimal digits
 *
 * Rounding behavior:
 * If numDecimalDigits is specified, round to the specified number of decimal digits.
 * Otherwise, no rounding is performed.
 */
export function safeSubtract(
  minuend: number,
  subtrahend: number,
  numDecimalDigits?: number,
): number {
  const result = new BigNumber(minuend).minus(subtrahend)
  return numDecimalDigits !== undefined
    ? result.decimalPlaces(numDecimalDigits).toNumber()
    : result.toNumber()
}

/**
 * Rounds to `numDigits` decimal digits with Excel's ROUND semantics: ties round away from zero,
 * a fractional digit count truncates toward zero, and a negative count rounds left of the decimal
 * point (safeRound(123.456, -2) === 100). Matches the SQL generators' ROUND, so form and table
 * formulas agree.
 */
export function safeRound(value: number, numDigits = 0): number {
  const digits = Math.trunc(numDigits)
  // BigNumber.decimalPlaces rejects a negative digit count, so shift the value instead:
  // round(x, -2) === round(x / 100) * 100, computed with exact decimal shifts.
  return new BigNumber(value)
    .shiftedBy(digits)
    .decimalPlaces(0, BigNumber.ROUND_HALF_UP)
    .shiftedBy(-digits)
    .toNumber()
}

/**
 * Given a cents value return the dollars equivalent.
 * i.e. Given 1234, return 12.34
 */
export function centsToDollars(cents: number): number {
  return new BigNumber(cents).dividedBy(100).decimalPlaces(2).toNumber()
}

/**
 * Given a dollar value return the cents equivalent.
 * i.e. Given 12.34, return 1234
 */
export function dollarsToCents(dollars: number): number {
  return new BigNumber(dollars).multipliedBy(100).toNumber()
}

export function formatNumberByLocale(
  number: number | string,
  locale: LocaleCode,
  options?: Intl.NumberFormatOptions,
): string {
  if (typeof number === 'string') {
    number = parseFloat(number)
  }
  if (isNaN(number)) {
    return ''
  }
  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: 4, // Default to 4 decimal places if not specified in `options`
    ...options,
    useGrouping: false,
  }).format(roundEpsilonToZeroForDisplay(number))
}

export function getFormattedNumberValue(
  value: number,
  formatOptions: FormatOptions | null | undefined,
  formatPreset: FormatPreset | null | undefined,
  locale: LocaleCode,
): string {
  if (isNaN(value)) {
    return ''
  }

  if (!formatPreset || !formatOptions) {
    return roundEpsilonToZeroForDisplay(value).toString()
  }

  value = roundEpsilonToZeroForDisplay(value, getRenderedDecimalPlaces(formatPreset, formatOptions))

  switch (formatPreset) {
    case FormatPreset.NUMBER_PLAIN:
    case FormatPreset.NUMBER_DECIMAL: {
      const { decimalPlaces, thousandsSeparator, negativeStyle } = formatOptions as FormatOptions<
        typeof FormatPreset.NUMBER_PLAIN
      >
      return applyNegativeStyle(
        value,
        negativeStyle,
        new Intl.NumberFormat(locale, {
          maximumFractionDigits: decimalPlaces,
          useGrouping: thousandsSeparator,
          signDisplay: 'never',
        }).format(value),
      )
    }
    case FormatPreset.NUMBER_INTEGER: {
      const { thousandsSeparator, negativeStyle } = formatOptions as FormatOptions<
        typeof FormatPreset.NUMBER_INTEGER
      >
      return applyNegativeStyle(
        value,
        negativeStyle,
        new Intl.NumberFormat(locale, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
          useGrouping: thousandsSeparator,
          signDisplay: 'never',
        }).format(value),
      )
    }
    case FormatPreset.NUMBER_PERCENT: {
      const { decimalPlaces, thousandsSeparator, negativeStyle } = formatOptions as FormatOptions<
        typeof FormatPreset.NUMBER_PERCENT
      >
      return applyNegativeStyle(
        value,
        negativeStyle,
        formatNumberAsPercentage(value, locale, {
          maximumFractionDigits: decimalPlaces,
          useGrouping: thousandsSeparator,
          signDisplay: 'never',
        }),
      )
    }
    case FormatPreset.NUMBER_CURRENCY: {
      const {
        currency,
        locale: currencyLocale,
        negativeStyle,
        decimalPlaces,
      } = formatOptions as FormatOptions<typeof FormatPreset.NUMBER_CURRENCY>
      return new Intl.NumberFormat(currencyLocale, {
        style: 'currency',
        currency,
        currencySign: negativeStyle === NumberNegativeStyle.ACCOUNTING ? 'accounting' : 'standard',
        ...(decimalPlaces !== undefined
          ? { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces }
          : {}),
      }).format(value)
    }
    case FormatPreset.NUMBER_UNIT: {
      const { unit, decimalPlaces, thousandsSeparator } = formatOptions as FormatOptions<
        typeof FormatPreset.NUMBER_UNIT
      >
      const formatted = new Intl.NumberFormat(locale, {
        maximumFractionDigits: decimalPlaces,
        useGrouping: thousandsSeparator,
      }).format(value)
      return `${formatted} ${unit}`
    }
    default:
      throw new DossError('Invalid preset for numeric values', [])
  }
}

export type NumberColumnFormat = {
  formatOptions?: unknown
  formatPreset?: FormatPreset | null
}

export function formatNumberColumnValue(
  value: unknown,
  columnFormat: NumberColumnFormat,
  locale: LocaleCode,
): string {
  const numericValue = coerceNumberValue(value)
  if (numericValue === undefined) {
    return ''
  }

  if (columnFormat.formatPreset) {
    const parsed = safeParseFormatOptions(columnFormat.formatPreset, columnFormat.formatOptions)
    if (parsed.success) {
      return getFormattedNumberValue(numericValue, parsed.data, columnFormat.formatPreset, locale)
    }
  }

  return formatNumberByLocale(numericValue, locale)
}

function coerceNumberValue(value: unknown): number | undefined {
  if (typeof value !== 'number' && typeof value !== 'string') {
    return undefined
  }

  const numericValue = typeof value === 'number' ? value : parseFloat(value)
  return Number.isNaN(numericValue) ? undefined : numericValue
}

function applyNegativeStyle(
  value: number,
  style: NumberNegativeStyle,
  formattedAbsoluteValue: string,
): string {
  if (value >= 0) {
    return formattedAbsoluteValue
  }
  return style === NumberNegativeStyle.ACCOUNTING
    ? `(${formattedAbsoluteValue})`
    : `-${formattedAbsoluteValue}`
}

/**
 * Formats a number for Elasticsearch search indexing. Uses the column's decimal places
 * configuration but omits symbols (currency, percent) and separators (thousands) to enable
 * intuitive prefix/contains matching.
 *
 * For example, a currency column displaying "$1,234.56" will be indexed as "1234.56",
 * allowing users to search for "1234" and find the value.
 */
export function formatNumberForSearch(value: number, columnFormat: NumberColumnFormat): string {
  if (!columnFormat.formatPreset) {
    return roundEpsilonToZeroForDisplay(value).toString()
  }

  const parsed = safeParseFormatOptions(columnFormat.formatPreset, columnFormat.formatOptions)
  if (!parsed.success) {
    return roundEpsilonToZeroForDisplay(value).toString()
  }

  const decimalPlaces = getRenderedDecimalPlaces(columnFormat.formatPreset, parsed.data)
  const roundedValue = roundEpsilonToZeroForDisplay(value, decimalPlaces)

  // For PERCENT preset, convert decimal to percentage value and apply decimal places
  if (columnFormat.formatPreset === FormatPreset.NUMBER_PERCENT) {
    return (roundedValue * 100).toFixed(decimalPlaces ?? 2)
  }

  // Format with fixed decimal places if specified, otherwise use toString
  if (decimalPlaces !== undefined) {
    return roundedValue.toFixed(decimalPlaces)
  }

  return roundedValue.toString()
}

export function parseNumberByLocale(numberString: string, locale: LocaleCode): number {
  const parserByLocale = new NumberParser(locale)
  return parserByLocale.parse(numberString)
}

/**
 * Parses a number string with that either has a comma or period as the decimal separator.
 *
 * This is useful for parsing numbers from CSV files where the locale is unknown.
 * If the string has a comma (European formats), it is replaced with a period before parsing,
 * otherwise it is parsed directly.
 * CSV exports usually don't contain formatted numbers with thousands separators,
 * so this function is sufficient for most cases. If there are thousands separators,
 * the function will return NaN or an incorrect value.
 *
 * If there are other characters in the string, the function will return NaN.
 */
export function parseNumberWithUnknownLocale(numberString: string): number {
  // If only a comma is present, likely European format
  if (numberString.includes(',') && numberString.includes('.')) {
    return NaN // Return NaN if thousands separator is present
  }
  if (numberString.includes(',')) {
    return parseFloat(numberString.replace(',', '.')) // Replace comma with period
  }
  return parseFloat(numberString)
}

/**
 * Formats a number as currency based on the locale and currency provided.
 *
 * Use abbreviate to format the number in a compact notation - i.e. 1.2M instead of 1,200,000.
 */
export function formatCurrency(
  amount: number | string,
  locale: LocaleCode,
  currency: string | undefined,
  abbreviate = false,
  decimals = 2,
): string {
  if (typeof amount === 'string') {
    amount = parseFloat(amount)
  }
  if (isNaN(amount)) {
    return ''
  }
  amount = roundEpsilonToZeroForDisplay(amount, decimals)

  if (abbreviate) {
    return new Intl.NumberFormat(locale, {
      style: currency ? 'currency' : 'decimal',
      notation: 'compact',
      compactDisplay: 'short',
      currency,
      maximumFractionDigits: decimals,
    }).format(amount)
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(amount)
}
